/* Native details and forms remain usable when JavaScript is unavailable. */
(() => {
  const charmControllers = new Map();
  const viewportCards = new Set();
  const touchViewport = window.matchMedia('(hover: none), (pointer: coarse)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const viewportCardSelector = '.ld-format, .ld-collection-tile, .ld-panel--all';
  let cardObserver;

  function removeViewportCard(card) {
    cardObserver?.unobserve(card);
    card.classList.remove('is-in-view');
    viewportCards.delete(card);
  }

  function initializeViewportCards() {
    const enabled = touchViewport.matches && !reducedMotion.matches && 'IntersectionObserver' in window;
    viewportCards.forEach(card => {
      if (!enabled || !card.isConnected) removeViewportCard(card);
    });
    if (!enabled) {
      cardObserver?.disconnect();
      cardObserver = null;
      return;
    }
    cardObserver ??= new IntersectionObserver(entries => {
      entries.forEach(entry => {
        // Start only when the entire card is visible, then reset on exit.
        if (!entry.isIntersecting) entry.target.classList.remove('is-in-view');
        else if (entry.intersectionRatio >= 1) entry.target.classList.add('is-in-view');
      });
    }, { threshold: [0, 1] });
    document.querySelectorAll(viewportCardSelector).forEach(card => {
      if (viewportCards.has(card)) return;
      viewportCards.add(card);
      cardObserver.observe(card);
    });
  }

  touchViewport.addEventListener('change', initializeViewportCards);
  reducedMotion.addEventListener('change', initializeViewportCards);

  function initializeCharm(header) {
    const charm = header.querySelector('[data-ld-bookmark-charm]');
    if (!charm || header.dataset.headSwing !== 'true') return;

    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let angle = 0;
    let velocity = 0;
    let frame = 0;
    let previousFrame = 0;
    let previousScroll = Math.max(0, window.scrollY);
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const reset = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      previousFrame = 0;
      angle = 0;
      velocity = 0;
      previousScroll = Math.max(0, window.scrollY);
      charm.style.removeProperty('--ld-charm-angle');
      charm.removeAttribute('data-swinging');
    };

    const animate = (time) => {
      if (motionPreference.matches || document.hidden || !header.isConnected) {
        reset();
        return;
      }
      // The chain and pendant pivot together at the fixed eyelet.
      // Use elapsed time so the motion feels consistent across refresh rates.
      const elapsed = previousFrame ? clamp((time - previousFrame) / 1000, 1 / 240, 1 / 30) : 1 / 60;
      previousFrame = time;
      velocity += (-52 * angle - 4.5 * velocity) * elapsed;
      angle = clamp(angle + velocity * elapsed, -12, 12);
      charm.style.setProperty('--ld-charm-angle', `${angle.toFixed(3)}deg`);
      if (Math.abs(angle) < 0.025 && Math.abs(velocity) < 0.025) {
        reset();
        return;
      }
      frame = requestAnimationFrame(animate);
    };

    const onScroll = () => {
      const scroll = Math.max(0, window.scrollY);
      const distance = clamp(scroll - previousScroll, -100, 100);
      previousScroll = scroll;
      if (motionPreference.matches || document.hidden || !distance) return;
      velocity = clamp(velocity + distance * 0.45, -180, 180);
      if (!frame) {
        charm.setAttribute('data-swinging', '');
        frame = requestAnimationFrame(animate);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    motionPreference.addEventListener('change', reset);
    document.addEventListener('visibilitychange', reset);
    charmControllers.set(header, () => {
      reset();
      window.removeEventListener('scroll', onScroll);
      motionPreference.removeEventListener('change', reset);
      document.removeEventListener('visibilitychange', reset);
      charmControllers.delete(header);
    });
  }

  function initializeHeader(header) {
    if (header.dataset.initialized) return;
    header.dataset.initialized = 'true';
    initializeCharm(header);
    const disclosures = [...header.querySelectorAll('.ld-disclosure')];
    const closing = new WeakMap();
    const close = (details, restoreFocus = false) => {
      if (closing.has(details)) return closing.get(details);
      const modal = details.querySelector('dialog[open]');
      const finish = () => {
        modal?.classList.remove('is-closing');
        modal?.close();
        if (details.classList.contains('ld-mobile-menu')) document.documentElement.classList.remove('ld-menu-open');
        details.open = false;
        if (restoreFocus) details.querySelector('summary').focus({ preventScroll: true });
      };
      if (!modal || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { finish(); return Promise.resolve(); }
      const completion = new Promise(resolve => {
        let timer;
        const done = () => { clearTimeout(timer); modal.removeEventListener('animationend', ended); finish(); closing.delete(details); resolve(); };
        const ended = event => { if (event.target === modal && event.animationName === 'ld-menu-page-exit') done(); };
        modal.addEventListener('animationend', ended);
        modal.classList.add('is-closing');
        timer = setTimeout(done, 450);
      });
      closing.set(details, completion);
      return completion;
    };

    disclosures.forEach((details) => {
      const modal = details.querySelector('dialog');
      if (details.classList.contains('ld-language')) {
        const hoverDesktop = window.matchMedia('(hover: hover) and (pointer: fine)');
        details.addEventListener('pointerenter', () => {
          if (hoverDesktop.matches) details.open = true;
        });
        details.addEventListener('pointerleave', () => {
          if (hoverDesktop.matches && !details.matches(':focus-within')) close(details);
        });
      }
      if (modal) {
        const dismiss = () => close(details, true);
        modal.querySelector('[data-ld-menu-close]').addEventListener('click', dismiss);
        modal.addEventListener('close', () => {
          details.open = false;
          document.documentElement.classList.remove('ld-menu-open');
          details.querySelector('summary').focus({ preventScroll: true });
        });
        modal.addEventListener('cancel', event => { event.preventDefault(); dismiss(); });
        modal.querySelectorAll('a').forEach(link => link.addEventListener('click', event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
          event.preventDefault();
          close(details, true).then(() => { window.location.href = link.href; });
        }));
      }
      details.addEventListener('toggle', () => {
        details.querySelector('summary').setAttribute('aria-expanded', String(details.open));
        if (!details.open) {
          modal?.close();
          return;
        }
        disclosures.forEach((other) => { if (other !== details) close(other); });
        if (modal) {
          modal.showModal();
          document.documentElement.classList.add('ld-menu-open');
          modal.querySelector('[data-ld-menu-close]').focus();
        } else details.querySelector('input[type="search"]')?.focus();
      });
      details.addEventListener('keydown', (event) => {
        if (modal?.open && event.key === 'Tab') {
          const controls = [...modal.querySelectorAll('a[href], button, input, select, textarea, [tabindex="0"]')].filter(el => !el.disabled && el.getClientRects().length);
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          close(details, true);
        }
      });
      details.addEventListener('focusout', () => {
        if (modal) return;
        requestAnimationFrame(() => {
          if (!details.contains(document.activeElement)) close(details);
        });
      });
    });
  }

  const init = () => {
    initializeViewportCards();
    charmControllers.forEach((dispose, header) => { if (!header.isConnected) dispose(); });
    document.querySelectorAll('[data-ld-header]').forEach(initializeHeader);
  };
  init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(viewportCardSelector).forEach(removeViewportCard);
    event.target.querySelectorAll('[data-ld-header]').forEach((header) => {
      header.querySelector('dialog[open]')?.close();
      charmControllers.get(header)?.();
      delete header.dataset.initialized;
    });
  });
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.ld-disclosure[open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });
})();
